import React, { FC, useState } from 'react';
import {
  useBi,
  useEnvironment,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import {
  GenericEventsBiLoggerProvider,
  PageContextProvider,
  useGenericEventsLogger,
} from '@wix/data-tools-generic-events';
import { useSettings } from '@wix/tpa-settings/react';
import {
  Button,
  ButtonPriority,
  Text,
  TextTypography as TYPOGRAPHY,
} from 'wix-ui-tpa/cssVars';
import { UnitType } from '@wix/ambassador-bookings-catalog-search-v1-catalog-search/types';
import { classes, st } from './Widget.st.css';
import { ServiceCard } from './ServiceCard/ServiceCard';
import { FiltersBar } from './FiltersBar/FiltersBar';
import { SearchBar } from './SearchBar/SearchBar';
import { RentalsListDataHooks } from './dataHooks';
import settingsParams from '../settingsParams';
import { BI_PAGE_NAME } from '../../../consts';
import type { FilterBarState, WidgetViewModel } from '../../../types/types';
import type { WidgetActions } from '../../../actions/actions';

export type ControllerProps = {
  widgetViewModel: WidgetViewModel;
  widgetActions: WidgetActions;
  filterState: FilterBarState;
  /** True while a filter change is in flight. */
  isLoading: boolean;
  isLoadingMore: boolean;
};

/**
 * Wraps `WidgetInner` with the generic-events (BI src:198) providers.
 * `useGenericEventsLogger()` must not run in the same component that
 * renders these providers, hence the split — see
 * `bi-events_generic-events-setup`'s context-timing rule.
 */
export const Widget: FC<WidgetProps<ControllerProps>> = (props) => {
  const bi = useBi();

  return (
    <GenericEventsBiLoggerProvider value={bi}>
      <PageContextProvider pageContext={{ pageName: BI_PAGE_NAME }}>
        <WidgetInner {...props} />
      </PageContextProvider>
    </GenericEventsBiLoggerProvider>
  );
};

const WidgetInner: FC<WidgetProps<ControllerProps>> = ({
  widgetViewModel,
  widgetActions,
  filterState,
  isLoading,
  isLoadingMore,
}) => {
  const { t } = useTranslation();
  const { isRTL, isMobile } = useEnvironment();
  const settings = useSettings();
  const logger = useGenericEventsLogger();
  const [popoverLayer, setPopoverLayer] = useState<HTMLDivElement | null>(null);

  const title = settings.get(settingsParams.showListTitle)
    ? settings.get(settingsParams.listTitle)
    : null;
  const showDailyRentals = settings.get(settingsParams.showDailyRentals);
  const showHourlyRentals = settings.get(settingsParams.showHourlyRentals);

  // The controller has not reported in yet (first paint before `setProps`).
  if (!widgetViewModel || !widgetActions || !filterState) {
    return null;
  }

  // Unit visibility is filtered here rather than in the query: the catalog
  // search has no unit filter, and the builder component hides them client-side
  // for the same reason.
  const services = widgetViewModel.services.filter(
    (service) =>
      !(
        (service.unit === UnitType.DAY && !showDailyRentals) ||
        (service.unit === UnitType.HOUR && !showHourlyRentals)
      ),
  );

  return (
    <div
      className={st(classes.root, { isRTL, isMobile })}
      data-hook={RentalsListDataHooks.root}
    >
      {/* Sheets render here: `.sizer` carries `container-type`, which makes it
          the containing block for `position: fixed`, so a drawer inside it
          would dock to the bottom of the widget instead of the viewport. */}
      <div className={classes.popoverLayer} ref={setPopoverLayer} />

      {/* The size container every breakpoint below queries. */}
      <div className={classes.sizer}>
        {title ? (
          <Text
            className={classes.title}
            data-hook={RentalsListDataHooks.title}
            typography={TYPOGRAPHY.largeTitle}
            tagName="h1"
          >
            {title}
          </Text>
        ) : null}

        <SearchBar
          filterState={filterState}
          locations={widgetViewModel.filterData.locations}
          onSearch={(next) => widgetActions.onFiltersChange(next)}
          disabled={isLoading}
          popoverLayer={popoverLayer}
        />

        <FiltersBar
          filterState={filterState}
          filterData={widgetViewModel.filterData}
          onChange={(next) => widgetActions.onFiltersChange(next)}
          disabled={isLoading}
          popoverLayer={popoverLayer}
        />

        {widgetViewModel.hasError ? (
          <Text
            className={classes.stateMessage}
            data-hook={RentalsListDataHooks.errorState}
            typography={TYPOGRAPHY.runningText}
          >
            {t('widget.servicesList.errorState')}
          </Text>
        ) : (
          <>
            {services.length === 0 ? (
              <Text
                className={classes.stateMessage}
                data-hook={RentalsListDataHooks.emptyState}
                typography={TYPOGRAPHY.runningText}
              >
                {widgetViewModel.nextCursor
                  ? t('widget.servicesList.emptyStateMorePages')
                  : t('widget.servicesList.emptyState')}
              </Text>
            ) : (
              <div
                className={classes.grid}
                data-hook={RentalsListDataHooks.grid}
              >
                {services.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onServiceClick={widgetActions.onServiceClick}
                  />
                ))}
              </div>
            )}

            {/* Outside the empty branch: the unit toggles filter each page
              client-side, so a page can be empty while later ones are not. */}
            {widgetViewModel.nextCursor ? (
              <Button
                className={classes.loadMore}
                data-hook={RentalsListDataHooks.loadMore}
                priority={ButtonPriority.basicSecondary}
                disabled={isLoading || isLoadingMore}
                onClick={() => {
                  logger.clickCta({ componentName: 'load_more_button' });
                  widgetActions.onLoadMore();
                }}
              >
                {settings.get(settingsParams.loadMoreButtonLabel)}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
